
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import QuestionSuggestionCard from './QuestionSuggestionCard.vue';

// Types from the insight model
interface QuestionSuggestion {
  id: string;
  question: string;
  instruction?: string;
  category?: string;
  complexity?: string;
  confidence?: number;
  rank?: number;
  relevance_score?: number;
  response_type?: string;
  why_relevant?: string;
}

interface SuggestionMetadata {
  generation_time_ms?: number;
  source?: string;
  model?: string;
  context_used?: string[];
  total_generated?: number;
}

interface FallbackInfo {
  reason?: string;
  generated_count?: number;
}

@Component({
  components: {
    QuestionSuggestionCard,
  },
})
export default class SuggestionLoader extends Vue {
  // Props - now includes suggestions instead of API calling parameters
  @Prop({ default: () => [] }) suggestions!: QuestionSuggestion[];
  @Prop({ default: null }) metadata!: SuggestionMetadata | null;
  @Prop({ default: null }) fallbackInfo!: FallbackInfo | null;
  @Prop({ default: false }) isLoading!: boolean;
  @Prop({ default: false }) hasError!: boolean;
  @Prop({ default: '' }) errorMessage!: string;
  @Prop({ default: false }) showSuggestionMetrics!: boolean;
  @Prop({ default: true }) showHoverActions!: boolean;
  @Prop({ default: false }) expandableSuggestions!: boolean;
  @Prop({ default: false }) isFallback!: boolean;
  @Prop({ default: true }) showRetry!: boolean;
  @Prop({ default: 5 }) initialDisplayCount!: number;

  // Component state (simplified - no API state)
  public displayedCount: number = 5;
  public isLoadingMore: boolean = false;
  public loadingStates: { [key: number]: boolean } = {};

  // Computed properties
  get shouldShowSuggestions(): boolean {
    return this.isLoading || this.hasError || this.suggestions.length > 0;
  }

  get displayedSuggestions(): QuestionSuggestion[] {
    return this.suggestions.slice(0, this.displayedCount);
  }

  get hasMoreSuggestions(): boolean {
    return this.suggestions.length > this.displayedCount;
  }

  get remainingSuggestions(): number {
    return this.suggestions.length - this.displayedCount;
  }

  get skeletonCount(): number {
    return this.initialDisplayCount;
  }

  get emptyMessage(): string {
    if (this.isFallback) {
      return 'No fallback suggestions available for this measure and dimension';
    }
    return 'No suggestions available. Try asking a question to get AI-powered suggestions.';
  }

  // Lifecycle
  mounted() {
    this.displayedCount = this.initialDisplayCount;
  }

  // Watchers - reset display count when suggestions change
  @Watch('suggestions')
  onSuggestionsChange() {
    this.displayedCount = Math.min(this.initialDisplayCount, this.suggestions.length);
    this.loadingStates = {}; // Clear loading states
  }

  // Event handlers
  public onSuggestionSelected(event: any): void {
    const { suggestion, question } = event;
    
    // Mark this suggestion as loading
    const index = this.suggestions.findIndex(s => s.question === suggestion.question);
    if (index !== -1) {
      this.$set(this.loadingStates, index, true);
      
      // Auto-clear loading state after a delay (in case parent doesn't notify)
      setTimeout(() => {
        this.$set(this.loadingStates, index, false);
      }, 3000);
    }
    
    this.$emit('suggestion-selected', { suggestion, question, index });
  }

  public onSuggestionToggled(event: any): void {
    this.$emit('suggestion-toggled', event);
  }

  public loadMoreSuggestions(): void {
    const newDisplayCount = Math.min(
      this.displayedCount + 3,
      this.suggestions.length
    );
    
    this.displayedCount = newDisplayCount;
    
    this.$emit('more-suggestions-displayed', {
      newCount: newDisplayCount,
      totalAvailable: this.suggestions.length
    });
  }

  public retryLoad(): void {
    this.$emit('retry-requested');
  }

  // Public methods for parent components
  public clearSuggestionLoading(index: number): void {
    this.$set(this.loadingStates, index, false);
  }

  public setSuggestionLoading(index: number, loading: boolean = true): void {
    this.$set(this.loadingStates, index, loading);
  }
}
